import { dayKey } from '@qr/shared/paths';
import { shortLinkOf } from '@qr/shared/slug';
import { fileUrl } from '@qr/shared/storage';
import type { UpdateQrCodeInput } from '@qr/shared/schemas';
import type { DailyStats, QrCode, QrDesign, RoutingRule } from '@qr/shared/types';
import { Button, Callout, Card, Field, Spinner } from '@qr/ui';
import { motion } from 'motion/react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  PayloadForm,
  payloadListo,
  type PayloadValue,
} from '../components/PayloadForms.js';
import {
  DesignEditor,
  DesignUpsell,
  disenoValido,
} from '../components/DesignEditor.js';
import { useCarpetas } from '../components/Carpetas.js';
import { descargarQr, QrPreview } from '../components/QrPreview.js';
import { ScanAnalytics, type RangoDias } from '../components/ScanAnalytics.js';
import { RoutingRules, RoutingUpsell } from '../components/RoutingRules.js';
import {
  Schedule,
  timestampToIso,
  type ScheduleValue,
} from '../components/Schedule.js';
import { api, messageFromError } from '../lib/api.js';
import { R2_PUBLIC_BASE, SHORT_LINK_BASE, db } from '../lib/firebase.js';
import { useCan, useOrg } from '../lib/org.js';
import { useConfirmar } from '../lib/dialogos.js';

/**
 * Direccion del logo de un codigo.
 *
 * La ruta manda sobre la direccion guardada. `logoUrl` solo lo tienen los
 * codigos creados antes de guardarse la ruta, y se sigue leyendo para que no
 * se queden sin logo.
 */
function logoDe(design: QrDesign | undefined): string {
  return fileUrl(design?.logoKey ?? design?.logoUrl, R2_PUBLIC_BASE);
}

type Pestana = 'destino' | 'diseno' | 'reglas' | 'vigencia' | 'analitica';

/**
 * Detalle de un codigo: cambiar el destino y ver que tal va.
 *
 * El cambio de destino es LA funcion del producto, asi que esta lo primero y
 * no escondida en una pestana de ajustes.
 */
export function CodeDetail() {
  const confirmar = useConfirmar();
  const { codeId } = useParams<{ codeId: string }>();
  const navigate = useNavigate();
  const canEdit = useCan('editor');
  const canDelete = useCan('admin');

  const [code, setCode] = useState<(QrCode & { id: string }) | null | undefined>(
    undefined,
  );
  const [pestana, setPestana] = useState<Pestana>('destino');

  useEffect(() => {
    if (!codeId) return;
    return onSnapshot(
      doc(db, `qrcodes/${codeId}`),
      (snap) => {
        setCode(snap.exists() ? { id: snap.id, ...(snap.data() as QrCode) } : null);
      },
      () => setCode(null),
    );
  }, [codeId]);

  if (code === undefined) {
    return (
      <div className="center">
        <Spinner label="Cargando el código" />
      </div>
    );
  }

  if (code === null) {
    return (
      <Card title="Código no encontrado">
        <p>Puede que se haya eliminado o que no tengas acceso.</p>
        <Link to="/">Volver a la lista</Link>
      </Card>
    );
  }

  const shortLink = shortLinkOf(code, SHORT_LINK_BASE);

  // El destino es LA función del producto, así que la primera pestaña siempre
  // está abierta. Las demás son ajustes que se tocan de vez en cuando, y
  // apiladas todas a la vez convertían esta pantalla en un muro.
  const pestanas = [
    { id: 'destino', nombre: code.target.kind === 'url' ? 'Destino' : 'Contenido' },
    { id: 'diseno', nombre: 'Diseño' },
    { id: 'reglas', nombre: 'Reglas' },
    { id: 'vigencia', nombre: 'Vigencia' },
    { id: 'analitica', nombre: 'Analítica' },
  ] as const;

  return (
    <div className="flex flex-col">
      {/* Mismas bandas que la lista: cabecera, pestañas y contenido. Que las
          dos pantallas se sostengan igual es lo que hace que pasar de una a
          otra no se note como un cambio de sitio. */}
      <header className="flex flex-col gap-2.5 border-b border-line px-4 py-3 lg:px-6">
        <Link
          to="/"
          className="inline-flex w-fit items-center gap-1.5 text-[12.5px] text-ink-3 no-underline hover:text-ink"
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M9.5 3.5L5 8l4.5 4.5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Todos los códigos
        </Link>

        <div className="flex flex-wrap items-center gap-3">
          <h1 className="m-0 min-w-0 truncate text-lg font-semibold tracking-tight 2xl:text-xl">
            {code.name}
          </h1>
          <EstadoCodigo code={code} />
          <span className="text-[12.5px] text-ink-3 tabular-nums">
            {code.scanCount.toLocaleString('es')}{' '}
            {code.scanCount === 1 ? 'escaneo' : 'escaneos'}
          </span>

          <div className="ms-auto flex items-center gap-2">
            {canEdit ? <SelectorDeCarpeta code={code} /> : null}
            <CopiarEnlace enlace={shortLink} />
            <a
              href={shortLink}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-sm border border-line px-3 py-1.5 text-[12.5px] font-medium text-ink-2 no-underline transition-colors hover:text-ink"
            >
              Abrir
            </a>
          </div>
        </div>
      </header>

      <div
        className="flex gap-1 overflow-x-auto border-b border-line px-4 lg:px-6"
        role="tablist"
      >
        {pestanas.map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={pestana === p.id}
            onClick={() => setPestana(p.id)}
            className={[
              'relative whitespace-nowrap px-3 py-2.5 text-[13.5px] transition-colors',
              pestana === p.id ? 'font-semibold text-ink' : 'text-ink-2 hover:text-ink',
            ].join(' ')}
          >
            {p.nombre}
            {pestana === p.id ? (
              <motion.span
                layoutId="pestana-activa"
                className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-brand"
              />
            ) : null}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-5 px-4 py-5 lg:px-6">
        {code.safety === 'flagged' ? (
          <Callout tone="danger" title="Código bloqueado por seguridad">
            Hemos detectado que el destino puede ser una página fraudulenta y hemos
            dejado de redirigir. Cambia el destino a una dirección segura para
            reactivarlo.
          </Callout>
        ) : null}

        <div className="grid items-start gap-5 lg:grid-cols-[280px_minmax(0,1fr)] 2xl:gap-6 2xl:grid-cols-[340px_minmax(0,1fr)] 3xl:grid-cols-[400px_minmax(0,1fr)]">
          {/* El código y su descarga se quedan a la vista mientras se navega
              por las pestañas: es lo que has venido a buscar si entras para
              llevarlo a imprenta. */}
          <aside className="flex flex-col gap-3.5 rounded-lg border border-line bg-surface p-4 2xl:p-5 lg:sticky lg:top-5">
            <div className="qr-fluido mx-auto w-full max-w-[220px] rounded bg-white p-2 2xl:max-w-[280px] 3xl:max-w-[330px]">
              <QrPreview
                value={shortLink}
                size={320}
                fileName={code.slug}
                showDownloads={false}
                foreground={code.design?.foreground}
                background={code.design?.background}
                moduleShape={code.design?.moduleShape}
                eyeShape={code.design?.eyeShape}
                logoUrl={logoDe(code.design)}
              />
            </div>

            <Descargas code={code} shortLink={shortLink} />

            <div className="border-t border-line pt-3">
              <p className="m-0 text-[11.5px] text-ink-3">Enlace corto</p>
              <p className="m-0 break-all font-mono text-[12.5px] text-ink-2">
                {shortLink.replace(/^https?:\/\//, '')}
              </p>
            </div>

            <p className="m-0 text-[12px] text-ink-3">
              El SVG es para imprenta: escala a cualquier tamaño sin perder
              nitidez. Lo impreso no caduca, y cambiar el destino no modifica ni un
              píxel del código.
            </p>
          </aside>

          {/* Cada pestaña tiene su ancho cómodo, y ninguno es "todo".
              Un campo de dirección de 1100 px hace perder el renglón al ojo.
              Una gráfica quiere más sitio, pero tampoco sin límite: a 1800 px
              treinta días quedan a sesenta píxeles uno de otro y el resultado
              es un lienzo enorme y casi vacío. */}
          <div
            className={`flex min-w-0 flex-col gap-4 ${
              pestana === 'analitica' ? 'max-w-[76rem]' : 'max-w-[46rem]'
            }`}
          >
            <div className="stack">
              {/* Un código de enlace se edita cambiando su destino; uno de los
                  tipos servidos por nosotros, cambiando su contenido. Nunca los
                  dos, porque solo uno de los dos significa algo en cada caso. */}
              {pestana === 'destino' && canEdit && code.target.kind === 'url' ? (
                <TargetForm code={code} />
              ) : null}
              {pestana === 'destino' && canEdit && code.target.kind === 'hosted' ? (
                <ContentForm code={code} />
              ) : null}
              {pestana === 'destino' && !canEdit ? (
                <Callout tone="info">
                  Tu rol es de solo lectura, así que puedes consultar este código
                  pero no cambiarlo.
                </Callout>
              ) : null}

              {pestana === 'diseno' && canEdit ? <DesignForm code={code} /> : null}
              {pestana === 'reglas' && canEdit ? <RoutingForm code={code} /> : null}
              {pestana === 'vigencia' && canEdit ? <ScheduleForm code={code} /> : null}
              {pestana === 'analitica' ? (
                <ScanStats codeId={code.id} scanCount={code.scanCount} />
              ) : null}
            </div>

            {canDelete ? (
              <details className="mt-2 rounded border border-line px-4 py-3 text-[13px]">
                <summary className="cursor-pointer text-ink-2">
                  Eliminar este código
                </summary>
                <div className="flex flex-col items-start gap-3 pt-3">
                  <p className="m-0 text-ink-2">
                    Al eliminarlo, cualquier código ya impreso dejará de funcionar. El
                    enlace corto queda libre y podría reutilizarse.
                  </p>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={async () => {
                      const confirmed = await confirmar({
                        titulo: `Eliminar "${code.name}"`,
                        mensaje:
                          'Los códigos ya impresos dejarán de funcionar: quien los escanee no llegará a ninguna parte. Esto no se puede deshacer.',
                        aceptar: 'Eliminar el código',
                        grave: true,
                      });
                      if (!confirmed) return;
                      await api.deleteQrCode({ codeId: code.id });
                      navigate('/', { replace: true });
                    }}
                  >
                    Eliminar código
                  </Button>
                </div>
              </details>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * A qué carpeta pertenece el código.
 *
 * Va en la cabecera y no dentro de una pestaña porque es una etiqueta del
 * código entero, no un ajuste de su destino ni de su diseño. Se guarda al
 * cambiarlo: no hay nada que confirmar en elegir una carpeta.
 */
function SelectorDeCarpeta({ code }: { readonly code: QrCode & { id: string } }) {
  const carpetas = useCarpetas(code.orgId);
  const [busy, setBusy] = useState(false);

  if (carpetas.length === 0) return null;

  return (
    <label className="flex items-center gap-1.5">
      <span className="sr-only">Carpeta</span>
      <select
        id={`carpeta-de-${code.id}`}
        value={code.folderId ?? ''}
        disabled={busy}
        className="rounded-sm border border-line bg-surface px-2.5 py-1.5 text-[12.5px] text-ink-2"
        onChange={async (e) => {
          setBusy(true);
          try {
            await api.updateQrCode({
              codeId: code.id,
              folderId: e.target.value === '' ? null : e.target.value,
            });
          } finally {
            setBusy(false);
          }
        }}
      >
        <option value="">Sin carpeta</option>
        {carpetas.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Estado del código, con la misma forma que en la lista. */
function EstadoCodigo({ code }: { readonly code: QrCode }) {
  if (code.safety === 'flagged') {
    return <span className="badge badge--danger">Bloqueado</span>;
  }
  if (!code.active) return <span className="badge badge--muted">Pausado</span>;
  if (code.safety === 'pending') {
    return <span className="badge badge--warning">Comprobando</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-ink-2">
      <span className="size-1.5 shrink-0 rounded-full bg-ok" />
      Activo
    </span>
  );
}

function CopiarEnlace({ enlace }: { readonly enlace: string }) {
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!copiado) return;
    const t = setTimeout(() => setCopiado(false), 1800);
    return () => clearTimeout(t);
  }, [copiado]);

  return (
    <button
      type="button"
      className="rounded-sm border border-line px-3 py-1.5 text-[12.5px] font-medium text-ink-2 transition-colors hover:text-ink"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(enlace);
          setCopiado(true);
        } catch {
          // Sin permiso de portapapeles (o sin https) no se puede copiar; el
          // enlace sigue a la vista en la columna de al lado.
        }
      }}
    >
      {copiado ? 'Copiado' : 'Copiar enlace'}
    </button>
  );
}

/**
 * Las dos descargas, cada una con su para qué.
 *
 * Estaban dentro de la vista previa y se perdían entre los controles. Aquí
 * tienen sitio propio y el texto dice cuál sirve para qué, que es la duda
 * real de quien va a mandar esto a imprenta.
 */
function Descargas({
  code,
  shortLink,
}: {
  readonly code: QrCode & { id: string };
  readonly shortLink: string;
}) {
  const [generando, setGenerando] = useState<'svg' | 'png' | null>(null);

  const diseno = {
    value: shortLink,
    foreground: code.design?.foreground,
    background: code.design?.background,
    moduleShape: code.design?.moduleShape,
    eyeShape: code.design?.eyeShape,
    logoUrl: logoDe(code.design),
  };

  async function bajar(extension: 'svg' | 'png') {
    setGenerando(extension);
    try {
      await descargarQr(diseno, code.slug, extension);
    } finally {
      setGenerando(null);
    }
  }

  const boton =
    'flex-1 rounded-sm border border-line py-2 text-center text-[12.5px] font-medium text-ink-2 transition-colors hover:text-ink disabled:opacity-60';

  return (
    <div className="flex gap-2">
      <button
        type="button"
        className={boton}
        disabled={generando !== null}
        onClick={() => void bajar('svg')}
      >
        {generando === 'svg' ? 'Generando…' : 'SVG'}
      </button>
      <button
        type="button"
        className={boton}
        disabled={generando !== null}
        onClick={() => void bajar('png')}
      >
        {generando === 'png' ? 'Generando…' : 'PNG'}
      </button>
    </div>
  );
}

function TargetForm({ code }: { readonly code: QrCode & { id: string } }) {
  const current = code.target.kind === 'url' ? code.target.url : '';
  const [url, setUrl] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Si el destino cambia desde fuera (otra pestana, un companero del equipo),
  // el campo se pone al dia. El ajuste se hace durante el render y no en un
  // efecto: asi React lo resuelve en la misma pasada, sin pintar antes el
  // valor viejo.
  const [seenTarget, setSeenTarget] = useState(current);
  if (current !== seenTarget) {
    setSeenTarget(current);
    setUrl(current);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      await api.updateQrCode({ codeId: code.id, targetUrl: url.trim() });
      setSaved(true);
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Destino"
      description="A donde llega quien escanea. El cambio tarda hasta un minuto en aplicarse."
    >
      <form onSubmit={handleSubmit} noValidate className="stack-sm">
        <Field
          label="Dirección de destino"
          type="url"
          value={url}
          onChange={(event) => {
            setUrl(event.target.value);
            setSaved(false);
          }}
          required
          error={error}
        />
        <div className="row">
          <Button type="submit" loading={busy} disabled={url.trim() === current}>
            Guardar destino
          </Button>
          {saved ? <span className="saved">Guardado</span> : null}
        </div>
      </form>

      <p className="muted small">
        Guardamos el destino anterior cada vez que lo cambias, por si hace falta
        consultarlo mas adelante.
      </p>
    </Card>
  );
}

/**
 * Contenido de los codigos que servimos nosotros: tarjeta, wifi o enlaces.
 *
 * Es el equivalente al destino en los codigos de enlace, y la razon de ser de
 * estos tipos: una tarjeta de contacto metida dentro del QR queda fija para
 * siempre, mientras que aqui se corrige un telefono sin volver a imprimir
 * nada.
 */
function ContentForm({ code }: { readonly code: QrCode & { id: string } }) {
  const inicial = (code.payload ?? {}) as PayloadValue;
  const [value, setValue] = useState<PayloadValue>(inicial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const serializado = JSON.stringify(inicial);
  const [visto, setVisto] = useState(serializado);
  if (serializado !== visto) {
    setVisto(serializado);
    setValue(inicial);
  }

  const dirty = JSON.stringify(value) !== serializado;
  const listo = payloadListo(code.type, value);

  async function save() {
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      await api.updateQrCode({ codeId: code.id, payload: value });
      setSaved(true);
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Contenido"
      description="Lo que ve quien escanea. Cambiarlo no modifica el código impreso."
    >
      <PayloadForm
        type={code.type}
        value={value}
        disabled={busy}
        orgId={code.orgId}
        onChange={setValue}
      />

      {error ? <Callout tone="danger">{error}</Callout> : null}

      <div className="row" style={{ marginBlockStart: 'var(--qr-space-4)' }}>
        <Button onClick={save} loading={busy} disabled={!dirty || !listo}>
          Guardar contenido
        </Button>
        {!listo ? (
          <span className="muted small">Faltan datos obligatorios.</span>
        ) : null}
        {saved && !dirty ? <span className="saved">Guardado</span> : null}
      </div>
    </Card>
  );
}

/**
 * Diseno del codigo.
 *
 * Es lo unico del panel que cambia el codigo impreso: tocar aqui obliga a
 * volver a descargarlo y a reimprimir. Por eso se dice con todas las letras,
 * y por eso la vista previa de arriba se actualiza sola al guardar.
 */
function DesignForm({ code }: { readonly code: QrCode & { id: string } }) {
  const org = useOrg();
  const [design, setDesign] = useState<QrDesign>(code.design);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const serializado = JSON.stringify(code.design);
  const [visto, setVisto] = useState(serializado);
  if (serializado !== visto) {
    setVisto(serializado);
    setDesign(code.design);
  }

  const allowed = org.limits.customDesign;
  const dirty = JSON.stringify(design) !== serializado;
  const valido = disenoValido(design);

  async function save() {
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      await api.updateQrCode({ codeId: code.id, design });
      setSaved(true);
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Diseño"
      description="Colores, formas y logo. Esto si cambia el código impreso."
    >
      {!allowed && !org.isComped ? (
        <DesignUpsell />
      ) : (
        <>
          <div className="diseno__previa">
            <QrPreview
              value={shortLinkOf(code, SHORT_LINK_BASE)}
              size={160}
              showDownloads={false}
              foreground={design.foreground}
              background={design.background}
              moduleShape={design.moduleShape}
              eyeShape={design.eyeShape}
              logoUrl={logoDe(design)}
            />
            <p className="muted small">
              Vista previa. Al guardar, vuelve a descargar el código: el que ya
              tengas descargado sigue con el diseño anterior.
            </p>
          </div>

          <DesignEditor
            orgId={code.orgId}
            value={design}
            disabled={busy}
            onChange={setDesign}
          />

          {error ? <Callout tone="danger">{error}</Callout> : null}

          <div className="row" style={{ marginBlockStart: 'var(--qr-space-4)' }}>
            <Button onClick={save} loading={busy} disabled={!dirty || !valido}>
              Guardar diseño
            </Button>
            {saved && !dirty ? <span className="saved">Guardado</span> : null}
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * Reglas de destino.
 *
 * Es funcion de pago. Cuando el plan no la incluye se muestra que existe y que
 * hace, en vez de esconderla: quien no sabe que algo existe no lo echa de
 * menos. En una cuenta de cortesia no se muestra ningun aviso de plan.
 */
function RoutingForm({ code }: { readonly code: QrCode & { id: string } }) {
  const org = useOrg();
  const [rules, setRules] = useState<RoutingRule[]>([...code.rules]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Se sincroniza con lo que llegue del servidor mientras no haya cambios sin
  // guardar, siguiendo el mismo patron que el destino.
  const [seen, setSeen] = useState(code.rules);
  if (code.rules !== seen) {
    setSeen(code.rules);
    setRules([...code.rules]);
  }

  const allowed = org.limits.dynamicRouting;
  const fallback = code.target.kind === 'url' ? code.target.url : '';
  const dirty = JSON.stringify(rules) !== JSON.stringify(code.rules);
  const incomplete = rules.some((rule) => rule.url.trim() === '');

  async function save() {
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      await api.updateQrCode({
        codeId: code.id,
        // El tipo de dominio declara los arrays como readonly para que nadie
        // los mute por accidente en el servidor; el esquema de entrada los
        // infiere mutables. En tiempo de ejecucion no hay diferencia, porque
        // esto viaja como JSON, asi que se clona y se ajusta el tipo aqui, en
        // el unico punto donde cruzan los dos mundos.
        rules: structuredClone(rules) as UpdateQrCodeInput['rules'],
      });
      setSaved(true);
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Reglas de destino"
      description="Manda a sitios distintos según quien escanee. Gana la primera regla que encaja."
    >
      {!allowed && !org.isComped ? (
        <RoutingUpsell />
      ) : (
        <>
          <RoutingRules
            rules={rules}
            fallbackUrl={fallback}
            disabled={busy}
            onChange={setRules}
          />

          {error ? <Callout tone="danger">{error}</Callout> : null}

          <div className="row" style={{ marginBlockStart: 'var(--qr-space-4)' }}>
            <Button onClick={save} loading={busy} disabled={!dirty || incomplete}>
              Guardar reglas
            </Button>
            {incomplete ? (
              <span className="muted small">Falta el destino de alguna regla.</span>
            ) : null}
            {saved && !dirty ? <span className="saved">Guardado</span> : null}
          </div>
        </>
      )}
    </Card>
  );
}

/** Fechas de vigencia y tope de escaneos. */
function ScheduleForm({ code }: { readonly code: QrCode & { id: string } }) {
  const initial: ScheduleValue = {
    from: timestampToIso(code.schedule?.from),
    to: timestampToIso(code.schedule?.to),
    scanCap: code.scanCap ?? null,
  };

  const [value, setValue] = useState<ScheduleValue>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const serialized = JSON.stringify(initial);
  const [seen, setSeen] = useState(serialized);
  if (serialized !== seen) {
    setSeen(serialized);
    setValue(initial);
  }

  const dirty = JSON.stringify(value) !== serialized;

  async function save() {
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      await api.updateQrCode({
        codeId: code.id,
        // `schedule` en null borra la programacion entera; con fechas se envia
        // el objeto. El servidor las convierte a Timestamp.
        schedule: value.from || value.to ? { from: value.from, to: value.to } : null,
        scanCap: value.scanCap,
      });
      setSaved(true);
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Vigencia"
      description="Cuando empieza, cuando termina y cuántos escaneos admite."
    >
      <Schedule
        value={value}
        scanCount={code.scanCount}
        disabled={busy}
        onChange={setValue}
      />

      {error ? <Callout tone="danger">{error}</Callout> : null}

      <div className="row" style={{ marginBlockStart: 'var(--qr-space-4)' }}>
        <Button onClick={save} loading={busy} disabled={!dirty}>
          Guardar vigencia
        </Button>
        {saved && !dirty ? <span className="saved">Guardado</span> : null}
      </div>
    </Card>
  );
}

/**
 * Analitica del codigo.
 *
 * Consulta solo los agregados diarios del periodo elegido. El panel nunca toca
 * los eventos en crudo: con un codigo popular serian cientos de miles de
 * documentos para pintar exactamente lo mismo.
 */
function ScanStats({
  codeId,
  scanCount,
}: {
  readonly codeId: string;
  readonly scanCount: number;
}) {
  const [rango, setRango] = useState<RangoDias>(30);
  const [dias, setDias] = useState<(DailyStats & { id: string })[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const desde = useMemo(() => {
    const fecha = new Date();
    fecha.setUTCDate(fecha.getUTCDate() - (rango - 1));
    return dayKey(fecha);
  }, [rango]);

  // Al cambiar de periodo se vacia para que vuelva a salir la rueda: los datos
  // del periodo anterior ya no valen. Se ajusta durante el render y no en un
  // efecto, para que React lo resuelva en la misma pasada en vez de pintar
  // primero las cifras viejas bajo el periodo nuevo.
  const [periodoVisto, setPeriodoVisto] = useState(rango);
  if (rango !== periodoVisto) {
    setPeriodoVisto(rango);
    setDias(null);
  }

  useEffect(() => {
    const q = query(
      collection(db, `stats/${codeId}/daily`),
      where('day', '>=', desde),
    );
    return onSnapshot(
      q,
      (snap) => {
        setDias(snap.docs.map((d) => ({ id: d.id, ...(d.data() as DailyStats) })));
        setError(null);
      },
      (caught) => {
        console.error('No se pudieron cargar las estadisticas', caught);
        setError(messageFromError(caught));
        setDias([]);
      },
    );
  }, [codeId, desde]);

  return (
    <ScanAnalytics
      dias={dias}
      rango={rango}
      onRango={setRango}
      totalHistorico={scanCount}
      error={error}
      nombreFichero={`escaneos-${codeId}`}
    />
  );
}
